import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col } from "react-bootstrap";
import Modal from "react-modal";
import {
  fetchTrainerTrialReportData,
  fetchTrainerTrialReportUsersData,
} from "../../../redux/plans-reducers/plansSlice";
import CmnFilters from "../../lead-management/common/CmnFilters";
import PaymentLinkTable from "../../payment-link-system/PaymentLinkTable";
import ModalHeadAndClose from "../../payouts-wtf/common/ModalHeadAndClose";
import ThreedayTrialHistory from "./ThreedayTrialHistory";
import ReactVortoxSpinner from "../../common-components/ReactVortoxSpinner";
import { convertStraight } from "../../common-data/Converts";
import { checkIsManagerRole } from "../../common-data/Conversions";

/**
 * Trainer Trial Report tab.
 * One row per trainer: total trials assigned / unique users / repeat users
 * (users the SAME trainer ran more than one trial for). Each number is
 * clickable and opens a drill-down list of the users behind it.
 */
const TrainerTrialReport = ({ userInfoData }) => {
  const dispatch = useDispatch();
  const {
    trainerTrialReport,
    trainerTrialReportLoading,
    trainerTrialReportUsers,
    trainerTrialReportUsersLoading,
  } = useSelector((state) => state.plans);

  const isAdmin = userInfoData?.account_type === "admin";
  const isGym = userInfoData?.account_type === "gym";
  const isMarketing = userInfoData?.account_type === "marketing";
  const isManagerRole = checkIsManagerRole(userInfoData);

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);

  const iniAllFilter = {
    selectedGym:
      !isAdmin && !isMarketing && !isManagerRole
        ? {
            label: isGym ? userInfoData?.name : userInfoData?.gym_name || "",
            value:
              userInfoData?.gym_id ||
              userInfoData?.user_id ||
              userInfoData?.uid ||
              "",
          }
        : "",
    selectedTrainer: "",
    selectedBCAStatus: "",
    selectedAssignedStatus: "",
    selectedDayNo: "",
    selectedCompletedCount: "",
    selectedSlotTime: "",
    threeDayDoneDateFrom: "",
    threeDayDoneDateTo: "",
    startDateFrom: "",
    startDateTo: "",
    searchString: "",
  };
  const [allFilters, setAllFilters] = useState(iniAllFilter);

  // Drill-down modal: { open, view, trainer }
  const iniDrill = { open: false, view: "", trainer: null };
  const [drill, setDrill] = useState(iniDrill);
  const [drillPage, setDrillPage] = useState(0);
  const [drillLimit, setDrillLimit] = useState(10);

  // Nested modal: a single user's full trial history
  const [historyModal, setHistoryModal] = useState({ open: false, user: null });
  const openHistory = (user) => {
    if (!user?.user_id) return;
    setHistoryModal({ open: true, user });
  };
  const closeHistory = () => setHistoryModal({ open: false, user: null });

  const reportFilters = {
    page: isPage + 1,
    limit: pageLimit || 10,
    gym_id: allFilters.selectedGym?.value || "",
    trainer_id: allFilters.selectedTrainer?.value || "",
    date_from: allFilters.startDateFrom || "",
    date_to: allFilters.startDateTo || "",
    search: appliedSearch || "",
  };

  // Fetch the per-trainer report whenever filters / pagination change
  useEffect(() => {
    dispatch(
      fetchTrainerTrialReportData({
        filters: reportFilters,
        token: userInfoData?.token,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPage,
    pageLimit,
    allFilters.selectedGym,
    allFilters.selectedTrainer,
    allFilters.startDateFrom,
    allFilters.startDateTo,
    appliedSearch,
    userInfoData?.token,
  ]);

  // Clear search when the box is emptied
  useEffect(() => {
    if (allFilters.searchString?.length === 0) {
      setSearchFilterApplied(false);
      setAppliedSearch("");
      setIsPage(0);
    }
  }, [allFilters.searchString]);

  const onClickSearch = () => {
    setIsPage(0);
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setAppliedSearch(allFilters.searchString);
    }
  };

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setAppliedSearch("");
    setSearchFilterApplied(false);
    setIsPage(0);
  };

  const reportData = trainerTrialReport?.data || [];
  const reportPagination = trainerTrialReport?.pagination || [];

  // ---- Drill-down ----
  const openDrill = (trainer, view) => {
    if (!trainer?.trainer_id) return;
    setDrillPage(0);
    setDrill({ open: true, view, trainer });
  };
  const closeDrill = () => {
    setDrill(iniDrill);
    setDrillPage(0);
  };

  useEffect(() => {
    if (!drill.open || !drill.trainer?.trainer_id) return;
    const filters = {
      trainer_id: drill.trainer.trainer_id,
      view: drill.view,
      page: drillPage + 1,
      limit: drillLimit || 10,
      gym_id: allFilters.selectedGym?.value || "",
      date_from: allFilters.startDateFrom || "",
      date_to: allFilters.startDateTo || "",
    };
    dispatch(
      fetchTrainerTrialReportUsersData({
        filters,
        token: userInfoData?.token,
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drill.open, drill.view, drill.trainer, drillPage, drillLimit]);

  const drillData = trainerTrialReportUsers?.data || [];
  const drillPagination = trainerTrialReportUsers?.pagination || [];

  const drillTitleMap = {
    total: "All Trials Assigned",
    unique: "Unique Users",
    repeat: "Repeat Users (duplicate trials)",
  };

  const ClickableCount = ({ value, view, trainer, className }) => (
    <span
      className={`trainer-report-count ${className || ""} ${
        Number(value) > 0 ? "is-clickable" : "is-zero"
      }`}
      onClick={() => Number(value) > 0 && openDrill(trainer, view)}
      style={{
        cursor: Number(value) > 0 ? "pointer" : "default",
        fontWeight: 700,
        textDecoration: Number(value) > 0 ? "underline" : "none",
      }}
    >
      {value || 0}
    </span>
  );

  const reportColumns = [
    {
      dataField: "trainer_name",
      text: "Trainer",
      formatter: (_, row) => (
        <div className="tb-data minWidth150">
          <span className="bold">{row?.trainer_name || "NA"}</span>
          <br />
          <small style={{ fontWeight: 500 }}>{row?.trainer_mobile || ""}</small>
          {row?.trainer_status && row.trainer_status !== "active" ? (
            <>
              <br />
              <small className="status-badge status-warning">
                {row.trainer_status}
              </small>
            </>
          ) : null}
        </div>
      ),
    },
    {
      dataField: "total_trials",
      text: "Total PT Trials",
      formatter: (_, row) => (
        <ClickableCount
          value={row?.total_trials}
          view="total"
          trainer={row}
          className="count-total"
        />
      ),
    },
    {
      dataField: "unique_users",
      text: "Unique Users",
      formatter: (_, row) => (
        <ClickableCount
          value={row?.unique_users}
          view="unique"
          trainer={row}
          className="count-unique"
        />
      ),
    },
    {
      dataField: "repeat_users",
      text: "Repeat Users (duplicate)",
      formatter: (_, row) => (
        <ClickableCount
          value={row?.repeat_users}
          view="repeat"
          trainer={row}
          className="count-repeat"
        />
      ),
    },
  ];

  const drillColumns = [
    {
      dataField: "user_name",
      text: "User",
      formatter: (_, row) => (
        <div className="tb-data minWidth150">
          <span
            className="bold"
            onClick={() => openHistory(row)}
            style={{
              cursor: "pointer",
              color: "#1976d2",
              textDecoration: "underline",
            }}
            title="View full trial history"
          >
            {row?.user_name || "NA"}
          </span>
          <br />
          <small style={{ textTransform: "lowercase", fontWeight: 500 }}>
            {row?.user_mobile || ""}
            <br />
            {row?.user_email || ""}
          </small>
        </div>
      ),
    },
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (_, row) => (
        <span className="tb-data minWidth150">{row?.gym_name || "NA"}</span>
      ),
    },
    {
      dataField: "user_trial_count",
      text: "Trials Taken",
      formatter: (_, row) => (
        <span className="tb-data" style={{ fontWeight: 700 }}>
          {row?.user_trial_count || 0}
        </span>
      ),
    },
    {
      dataField: "subscription_status",
      text: "Trial Status",
      formatter: (_, row) => (
        <span
          className={`status-badge ${
            row?.subscription_status === "active"
              ? "status-success"
              : row?.subscription_status === "expired"
              ? "status-danger"
              : "status-warning"
          }`}
        >
          {row?.subscription_status || "NA"}
        </span>
      ),
    },
    {
      dataField: "session_status",
      text: "Sessions Done",
      formatter: (_, row) => (
        <span className="tb-data">{row?.session_status || "0"}</span>
      ),
    },
    {
      dataField: "date_added",
      text: "Trial Assigned Date",
      formatter: (_, row) => (
        <div className="tb-data minWidth150">
          <span>
            {row?.date_added ? convertStraight(row.date_added) : "NA"}
          </span>
          {row?.start_date ? (
            <>
              <br />
              <small>Start: {convertStraight(row.start_date)}</small>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="trainer-trial-report">
      <Row>
        <Col>
          <CmnFilters
            from={"three-day-trial"}
            userInfoData={userInfoData}
            onClickReset={onResetFilters}
            filters={allFilters}
            setFilters={setAllFilters}
            onClickSearch={onClickSearch}
            searchFilterApplied={searchFilterApplied}
            searchPlaceHolder="Search by trainer name / mobile..."
            isLoading={trainerTrialReportLoading}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          {trainerTrialReportLoading ? (
            <div className="text-center py-4">
              <ReactVortoxSpinner />
            </div>
          ) : (
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt30">
              <PaymentLinkTable
                tableData={reportData}
                tableColumns={reportColumns}
                tablePagination={reportPagination}
                isPage={isPage}
                setIsPage={setIsPage}
                pageLimit={pageLimit}
                setPageLimit={setPageLimit}
                isFirstColSticky={false}
              />
            </div>
          )}
        </Col>
      </Row>

      <Modal
        isOpen={drill.open}
        onRequestClose={closeDrill}
        contentLabel="Trainer Trial Report Drill-down"
        ariaHideApp={false}
      >
        <div className="createNewCTOModalContainer mod-width-95p mod-height-95vh">
          <ModalHeadAndClose
            title={`${drill.trainer?.trainer_name || "Trainer"} — ${
              drillTitleMap[drill.view] || ""
            }`}
            onClose={closeDrill}
          />
          {trainerTrialReportUsersLoading ? (
            <div className="text-center py-4">
              <ReactVortoxSpinner />
            </div>
          ) : (
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt30">
              <PaymentLinkTable
                tableData={drillData}
                tableColumns={drillColumns}
                tablePagination={drillPagination}
                isPage={drillPage}
                setIsPage={setDrillPage}
                pageLimit={drillLimit}
                setPageLimit={setDrillLimit}
                isFirstColSticky={false}
              />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={historyModal.open}
        onRequestClose={closeHistory}
        contentLabel="User Trial History"
        ariaHideApp={false}
        style={{ overlay: { zIndex: 1100 } }}
      >
        <div className="createNewCTOModalContainer mod-width-95p mod-height-95vh">
          <ModalHeadAndClose
            title={`${historyModal.user?.user_name || "User"} — Trial History`}
            onClose={closeHistory}
          />
          {historyModal.open ? (
            <ThreedayTrialHistory
              rowData={historyModal.user}
              userInfoData={userInfoData}
            />
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default TrainerTrialReport;
